/** Mirrors ImportBudgetResultDTO from AnsokanApi. */
export interface BudgetImportResult {
  /** 'SEK' | 'TKR' | 'MSEK' as reported by the extraction. */
  unit: BudgetImportUnit;
  startYear: number;
  endYear: number;
  projektparter: BudgetImportProjektpart[];
  cells: BudgetImportCell[];
  unmatchedBlocks: string[];
  /** Workbook-wide issues only; per-part issues are on each projektpart. */
  warnings: string[];
}

export type BudgetImportUnit = 'SEK' | 'TKR' | 'MSEK';

/** Mirrors the reasoning.effort values accepted by AnsokanApi; undefined uses the server default. */
export type BudgetImportReasoningEffort =
  | 'none'
  | 'minimal'
  | 'low'
  | 'medium'
  | 'high';

export const BUDGET_IMPORT_UNIT_FACTOR: Record<BudgetImportUnit, number> = {
  SEK: 1,
  TKR: 1000,
  MSEK: 1_000_000,
};

export interface BudgetImportProjektpart {
  id: string;
  koordinerande: boolean;
  namn?: string;
  /** The worksite's own name, distinct from the company name in `namn`; only present in results, never echoed back by the backend. */
  arbetsstalleNamn?: string;
  organisationsnummer?: string;
  adress?: string;
  /** Named financiers (e.g. "Region Skåne", "EU") attached to this projektpart; each gets its own budget row. */
  finansiarer?: BudgetImportFinansiar[];
  /** 0..1 for the part's imported values as a whole; only present in results, never in the schema. */
  confidence?: number;
  /** Why the confidence is below 0.8, as reported by the extraction. */
  confidenceReason?: string;
  /** Things to double-check for this part; only present in results. */
  warnings?: string[];
}

/** A named financier row under a projektpart, distinct from the fixed kostnader/bidrag rows. */
export interface BudgetImportFinansiar {
  id: string;
  namn?: string;
}

/** Mirrors BudgetSlotSchema in AnsokanApi: the closed set of parter and years the extraction may fill. */
export interface BudgetSlotSchema {
  startYear: number;
  endYear: number;
  projektparter: BudgetImportProjektpart[];
}

/** 1-5 = kostnadsslag (Kostnadstyp), 101 = sökt bidrag från Vinnova. */
export const BUDGET_IMPORT_ROW_SOKT_BIDRAG = 101;

export interface BudgetImportCell {
  projektpartId: string;
  /** Set for a fixed-row slot. Exactly one of rowType/finansiarId is set, never both. */
  rowType?: number;
  /** Set for a named-financier slot (an id from that part's finansiarer[]). Exactly one of rowType/finansiarId is set, never both. */
  finansiarId?: string;
  ar: number;
  /** Amount as written in the sheet, before unit scaling. */
  summa: number;
  sourceSheet?: string;
  sourceCell?: string;
}

/** What the user confirmed in the preview; already scaled to SEK. */
export interface BudgetImportApplyResult {
  projektparter: BudgetImportApplyProjektpart[];
}

export interface BudgetImportApplyProjektpart {
  id: string;
  /** kostnadsslag id ('1'..'5') -> year -> SEK */
  kostnader: Record<string, Record<number, number>>;
  /** year -> SEK */
  bidrag: Record<number, number>;
  /** finansiar id -> year -> SEK */
  finansiarer: Record<string, Record<number, number>>;
}
